import React, { useState } from 'react';
import { 
  Ruler, 
  FileText, 
  Calendar,
  User,
  Clock,
  CheckCircle,
  AlertTriangle,
  Plus,
  Eye,
  Download,
  Settings
} from 'lucide-react';
import Card from '../../components/Card';

const EngineeringProjects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Mock data pentru proiecte CAD active
  const projects = [
    {
      id: 'TIBECO-001',
      name: 'Ansamblu Rezidențial Primăverii',
      client: 'SC Construct Nord SRL',
      type: 'Ferestre PVC',
      engineer: 'Ionel Filip',
      status: 'in_progress',
      priority: 'high',
      windows: 45,
      doors: 8,
      startDate: '2024-12-15',
      deadline: '2025-01-30',
      progress: 75,
      drawings: 12,
      approved: 9,
      pending: 3,
      budget: 89000,
      description: '45 ferestre PVC triple glazing + 8 uși de intrare pentru complex rezidențial'
    },
    {
      id: 'TIBECO-002',
      name: 'Vila Moderna Baneasa',
      client: 'Familia Popescu',
      type: 'Ferestre Aluminiu',
      engineer: 'Ionel Filip',
      status: 'review',
      priority: 'medium',
      windows: 18,
      doors: 3,
      startDate: '2024-12-20',
      deadline: '2025-02-15',
      progress: 45,
      drawings: 8,
      approved: 4,
      pending: 4,
      budget: 42000,
      description: 'Ferestre panoramice aluminiu cu sticla controlata termic'
    },
    {
      id: 'TIBECO-003',
      name: 'Birouri Corporate Center',
      client: 'XYZ Business Solutions',
      type: 'Curtain Wall',
      engineer: 'Mihai Radu',
      status: 'completed',
      priority: 'high',
      windows: 120,
      doors: 15,
      startDate: '2024-11-01',
      deadline: '2024-12-31',
      progress: 100,
      drawings: 25,
      approved: 25,
      pending: 0,
      budget: 180000,
      description: 'Sistem curtain wall pentru clădire birouri 8 etaje'
    },
    {
      id: 'TIBECO-004',
      name: 'Școala Generală Nr. 15',
      client: 'Primăria Sector 3',
      type: 'Ferestre PVC',
      engineer: 'Ionel Filip',
      status: 'planning',
      priority: 'medium',
      windows: 80,
      doors: 20,
      startDate: '2025-01-10',
      deadline: '2025-03-15',
      progress: 15,
      drawings: 15,
      approved: 2,
      pending: 13,
      budget: 95000,
      description: 'Renovare completa ferestre și uși pentru unitate de învățământ'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'review': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'planning': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Finalizat';
      case 'in_progress': return 'În Execuție';
      case 'review': return 'În Aprobare';
      case 'planning': return 'Planificare';
      default: return 'Necunoscut';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const totalProjects = projects.length;
  const activeProjects = projects.filter(p => p.status !== 'completed').length;
  const completedThisMonth = projects.filter(p => p.status === 'completed').length;
  const totalBudget = projects.reduce((sum, p) => sum + p.budget, 0);

  const ProjectModal = ({ project, onClose }) => {
    if (!project) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl m-4 max-h-screen overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-primary-600" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {project.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">ID: {project.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Detalii Proiect
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Client:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {project.client}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Tip:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {project.type}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Inginer:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {project.engineer}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Ferestre:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {project.windows} buc
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Uși:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {project.doors} buc
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Timeline și Progres
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Început:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(project.startDate).toLocaleDateString('ro-RO')}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Deadline:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(project.deadline).toLocaleDateString('ro-RO')}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">Progres:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {project.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Desene Tehnice
                </h4>
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-primary-600">
                      {project.drawings}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300">Total</div>
                  </div>
                  <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {project.approved}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300">Aprobate</div>
                  </div>
                  <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">
                      {project.pending}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300">În Așteptare</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Buget Proiect
                </h4>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-2">
                    {project.budget.toLocaleString('ro-RO')} €
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Valoare totală estimată
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Status și Prioritate
                </h4>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-600 dark:text-gray-300">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>
                    {getStatusText(project.status)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Prioritate:</span>
                  <AlertTriangle className={`w-5 h-5 ${getPriorityColor(project.priority)}`} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
              Descriere Proiect
            </h4>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
              {project.description}
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Închide
            </button>
            <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              Editează Proiect
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <FileText className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Proiecte Inginerie
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Management proiecte CAD și desene tehnice TIBECO
            </p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <Plus className="w-4 h-4" />
          Proiect Nou
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalProjects}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Proiecte Totale
              </div>
            </div>
            <FileText className="w-8 h-8 text-primary-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {activeProjects}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Proiecte Active
              </div>
            </div>
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {completedThisMonth}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Finalizate Luna
              </div>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-primary-600">
                {totalBudget.toLocaleString('ro-RO')}€
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Valoare Totală
              </div>
            </div>
            <Ruler className="w-8 h-8 text-primary-600" />
          </div>
        </div>
      </div>

      {/* Projects Table */}
      <Card title="Proiecte Active" icon={FileText}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                  Proiect
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                  Client
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                  Inginer
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                  Status
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                  Progres
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                  Deadline
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">
                  Acțiuni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="py-4 px-4">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {project.name}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {project.id} • {project.type}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-gray-900 dark:text-white">
                      {project.client}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        {project.engineer}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>
                        {getStatusText(project.status)}
                      </span>
                      <AlertTriangle className={`w-4 h-4 ${getPriorityColor(project.priority)}`} />
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {project.progress}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-white text-sm">
                        {new Date(project.deadline).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedProject(project);
                          setShowModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                        title="Vezi detalii"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Download planuri"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Configurări"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal */}
      {showModal && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default EngineeringProjects;